import React, { useEffect, useMemo, useState } from 'react';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import MediaAssetPicker from './MediaAssetPicker';
import { mediaUrl } from '../media/MediaAssetLibrary';
import WgiymEditor from '../ui/WgiymEditor';
import { CrmButton, CrmInput, CrmSelect, CrmSwitch } from '../ui';
import { getCommunicationTemplateVariables, previewCommunicationTemplate, testCommunicationTemplate } from '../../services/communicationService';

const message = (error) => error?.response?.data?.error || error?.message || 'Request failed.';
const nestSamples = (samples) => {
  const result = {};
  Object.entries(samples || {}).forEach(([key, value]) => {
    if (key.startsWith('payload.')) result.payload = { ...(result.payload || {}), [key.slice(8)]: value };
    else result[key] = value;
  });
  return result;
};

export const DEFAULT_EMAIL_DESIGN = {
  use_layout: true,
  logo_url: '',
  logo_asset_id: '',
  logo_alt: 'Fusion Box',
  logo_width: 160,
  accent_color: '#f26a21',
  background_color: '#f5f5f2',
  content_background_color: '#ffffff',
  footer_text: 'Fusion Box',
};

export default function EmailTemplateDesigner({ form, setForm, providers = [] }) {
  const [variables, setVariables] = useState([]);
  const [samples, setSamples] = useState({});
  const [insertInto, setInsertInto] = useState('body_html');
  const [preview, setPreview] = useState(null);
  const [previewMode, setPreviewMode] = useState('desktop');
  const [previewError, setPreviewError] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [testProvider, setTestProvider] = useState('');
  const [testState, setTestState] = useState({ sending: false, message: '', error: '' });
  const design = useMemo(() => ({ ...DEFAULT_EMAIL_DESIGN, ...(form.design_json || {}) }), [form.design_json]);

  useEffect(() => {
    let active = true;
    getCommunicationTemplateVariables(form.trigger_key).then((result) => {
      if (!active) return;
      const next = result?.variables || [];
      setVariables(next);
      setSamples((current) => Object.fromEntries(next.map((item) => [item.key, current[item.key] ?? item.sample])));
    }).catch((error) => active && setPreviewError(message(error)));
    return () => { active = false; };
  }, [form.trigger_key]);

  useEffect(() => {
    if (!form.subject_template && !form.body_html) { setPreview(null); return undefined; }
    const timer = window.setTimeout(async () => {
      try {
        setPreviewError('');
        setPreview(await previewCommunicationTemplate({ ...form, design_json: design, sample_variables: nestSamples(samples) }));
      } catch (error) {
        setPreview(null);
        setPreviewError(message(error));
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [form, design, samples]);

  const updateDesign = (patch) => setForm((current) => ({ ...current, design_json: { ...DEFAULT_EMAIL_DESIGN, ...(current.design_json || {}), ...patch } }));
  const insertVariable = (token) => setForm((current) => ({ ...current, [insertInto]: `${current[insertInto] || ''}${insertInto === 'body_html' ? `<span>${token}</span>` : token}` }));
  const nestedSamples = useMemo(() => nestSamples(samples), [samples]);

  const sendTest = async () => {
    setTestState({ sending: true, message: '', error: '' });
    try {
      const result = await testCommunicationTemplate({ recipient_email: testEmail, provider_profile_id: testProvider || null, template: { ...form, design_json: design }, sample_variables: nestedSamples });
      setTestState({ sending: false, message: result?.mode === 'console' ? 'Test rendered in console mail mode.' : `Test sent to ${result.recipient}.`, error: '' });
    } catch (error) {
      setTestState({ sending: false, message: '', error: message(error) });
    }
  };

  return (
    <div className="email-designer phase13-form-span-2">
      <div className="email-designer__workspace">
        <div className="email-designer__controls">
          <section className="email-designer__section">
            <div><h4>Message</h4><p>Build the subject and email content. Variables are replaced when the message is sent.</p></div>
            <CrmInput label="Subject" value={form.subject_template || ''} onChange={(event) => setForm((current) => ({ ...current, subject_template: event.target.value }))} />
            <div><span className="phase13-field-label">Email body</span><WgiymEditor value={form.body_html || ''} onChange={(value) => setForm((current) => ({ ...current, body_html: value }))} placeholder="Write the email…" /></div>
          </section>

          <section className="email-designer__section">
            <div className="email-designer__section-heading"><div><h4>Handlebars variables</h4><p>Choose where to insert, then click a variable.</p></div><CrmSelect label="Insert into" value={insertInto} onChange={(event) => setInsertInto(event.target.value)}><option value="body_html">Email body</option><option value="subject_template">Subject</option></CrmSelect></div>
            <div className="email-designer__tokens">{variables.map((item) => <button type="button" key={item.key} onClick={() => insertVariable(item.token)} title={item.label}><span>{item.label}</span><code>{item.token}</code></button>)}</div>
          </section>

          <section className="email-designer__section">
            <div><h4>Branding and layout</h4><p>Add a logo and control the reusable email frame.</p></div>
            <CrmSwitch label="Use branded email layout" checked={Boolean(design.use_layout)} onChange={(event) => updateDesign({ use_layout: event.target.checked })} />
            <MediaAssetPicker label="Header logo" mediaType="IMAGE" value={design.logo_asset_id || ''} currentUrl={design.logo_url || ''} helperText="Choose a publicly accessible logo from the media library." onChange={(assetId, asset) => updateDesign({ logo_asset_id: assetId || '', logo_url: asset?.url ? mediaUrl(asset.url) : '' })} />
            <div className="email-designer__grid"><CrmInput label="Logo URL" value={design.logo_url} onChange={(event) => updateDesign({ logo_url: event.target.value, logo_asset_id: '' })} /><CrmInput label="Logo alt text" value={design.logo_alt} onChange={(event) => updateDesign({ logo_alt: event.target.value })} /><CrmInput type="number" min="60" max="320" label="Logo width (px)" value={design.logo_width} onChange={(event) => updateDesign({ logo_width: Number(event.target.value || 160) })} /><CrmInput label="Footer text" value={design.footer_text} onChange={(event) => updateDesign({ footer_text: event.target.value })} /></div>
            <div className="email-designer__colors"><label>Accent <input type="color" value={design.accent_color} onChange={(event) => updateDesign({ accent_color: event.target.value })} /></label><label>Page <input type="color" value={design.background_color} onChange={(event) => updateDesign({ background_color: event.target.value })} /></label><label>Content <input type="color" value={design.content_background_color} onChange={(event) => updateDesign({ content_background_color: event.target.value })} /></label></div>
          </section>

          <section className="email-designer__section">
            <div><h4>Preview data</h4><p>Edit sample values to test how longer or missing data behaves.</p></div>
            <div className="email-designer__grid">{variables.map((item) => <CrmInput key={item.key} label={item.label} value={samples[item.key] ?? ''} onChange={(event) => setSamples((current) => ({ ...current, [item.key]: event.target.value }))} />)}</div>
          </section>

          <section className="email-designer__section">
            <div><h4>Send a test</h4><p>Sends the current unsaved design with sample data.</p></div>
            <div className="email-designer__test"><CrmInput type="email" label="Recipient email" value={testEmail} onChange={(event) => setTestEmail(event.target.value)} /><CrmSelect label="Email provider" value={testProvider} onChange={(event) => setTestProvider(event.target.value)}><option value="">Auto resolve</option>{providers.filter((item) => item.channel === 'EMAIL' && item.is_enabled).map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}</CrmSelect><CrmButton startIcon={<SendOutlinedIcon />} disabled={testState.sending || !testEmail} onClick={sendTest}>{testState.sending ? 'Sending…' : 'Send test'}</CrmButton></div>
            {testState.message ? <div className="email-designer__success">{testState.message}</div> : null}{testState.error ? <div className="phase13-error">{testState.error}</div> : null}
          </section>
        </div>

        <aside className="email-designer__preview">
          <div className="email-designer__preview-head"><div><strong>Live preview</strong><span>{preview?.subject || 'Add a subject'}</span></div><div><button type="button" className={previewMode === 'desktop' ? 'is-active' : ''} onClick={() => setPreviewMode('desktop')}>Desktop</button><button type="button" className={previewMode === 'mobile' ? 'is-active' : ''} onClick={() => setPreviewMode('mobile')}>Mobile</button></div></div>
          {previewError ? <div className="phase13-error">{previewError}</div> : null}
          <div className={`email-designer__frame is-${previewMode}`}>{preview?.html ? <iframe title="Email template preview" sandbox="" srcDoc={preview.html} /> : <div className="email-designer__blank">Start writing to see the email preview.</div>}</div>
        </aside>
      </div>
    </div>
  );
}
